import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import ThemeBackground from '../components/Layout/ThemeBackground';
import { Button, Typography, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { styled } from '@mui/material/styles';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from '../context/AuthContext';
import '../index.css';

const DatePickerWrapper = styled('div')(({ theme }) => ({
  position: 'relative',
  '& .react-datepicker-popper': {
    zIndex: 9999,
  },
  '& .react-datepicker': {
    fontFamily: theme.typography.fontFamily,
    zIndex: 9999,
  },
  '& .react-datepicker-wrapper': {
    width: '100%',
    display: 'block'
  },
  '& .react-datepicker__input-container': {
    width: '100%',
  },
}));

// Define the interface for client data
interface Client {
  referralCategory: 'direct' | 'organisation' | '';
  referralType: string;
  organisation: string;
  organisationDisplayName: string;
  numberOfSessions: string;
  sessionRate: string;
  firstName: string;
  lastName: string;
  phone: string;
  initialAssessment: string;
  assessmentDateTime: string;
  isExistingClient: boolean;
  currentCounsellorId?: string;
}

// Define the interface for lookup data
interface Lookup {
  _id: string;
  value: string;
  displayName: string;
  active: boolean;
  sessionRate?: number;
  numberOfSessions?: number;
  sortOrder?: number;
}

// Define the interface for form validation errors
interface Errors {
  firstName?: string;
  lastName?: string;
  phone?: string;
  referralType?: string;
  organisation?: string;
  assessmentDateTime?: string;
  currentCounsellorId?: string;
}

function Onboarding() {
  const [client, setClient] = useState<Client>({
    referralCategory: '',
    organisation: '',
    organisationDisplayName: '',
    numberOfSessions: '',
    sessionRate: '',
    firstName: '',
    lastName: '',
    phone: '',
    initialAssessment: '',
    assessmentDateTime: '',
    referralType: '',
    isExistingClient: false,
    currentCounsellorId: ''
  });

  const [organisations, setorganisations] = useState<Lookup[]>([]);
  const [referralTypes, setReferralTypes] = useState<Lookup[]>([]);
  const [sessionRates, setSessionRates] = useState<Lookup[]>([]);
  const [counsellors, setCounsellors] = useState<any[]>([]);
  const [errors, setErrors] = useState<Errors>({});
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [tempDate, setTempDate] = useState<Date | null>(null);

  const { user, isAuthenticated } = useAuth();

  // Fetch organisations and referral types on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check authentication instead of session storage
        if (!isAuthenticated || !user) {
          console.log('User not authenticated');
          return;
        }
        
        // Fetch organisations
        const orgResponse = await api.get('/api/lookups/admin/organisations');
        console.log('organisations response:', orgResponse.data);
        
        let orgs = [];
        if (orgResponse.data?.organisations) {
          orgs = orgResponse.data.organisations;
        } else if (Array.isArray(orgResponse.data)) {
          orgs = orgResponse.data;
        }
        console.log('Processed organisations:', orgs);
        setorganisations(orgs);

        // Fetch referral types
        const refResponse = await api.get('/api/lookups/admin/referralTypes');
        console.log('Referral types response:', refResponse.data);
        
        let types = [];
        if (refResponse.data?.referralTypes) {
          types = refResponse.data.referralTypes;
        } else if (Array.isArray(refResponse.data)) {
          types = refResponse.data;
        }
        console.log('Processed referral types:', types);
        setReferralTypes(types);

        // Fetch session rates
        const ratesResponse = await api.get('/api/lookups/admin/sessionRates');
        let rates = [];
        if (ratesResponse.data?.sessionRates) {
          rates = ratesResponse.data.sessionRates;
        } else if (Array.isArray(ratesResponse.data)) {
          rates = ratesResponse.data;
        }
        setSessionRates(rates.filter((r: Lookup) => r.active));

        // Fetch available counsellors for existing client assignment
        const counsellorsResponse = await api.get('/api/counsellors/available');
        setCounsellors(counsellorsResponse.data || []);
      } catch (error: any) {
        console.error('Error fetching data:', error);
        const errorMessage = error?.response?.data?.error || error.message || 'Failed to fetch data';
        console.error(errorMessage);
        // Initialize empty arrays on error
        setorganisations([]);
        setReferralTypes([]);
        setSessionRates([]);
        setCounsellors([]);
      }
    };

    fetchData();
  }, [isAuthenticated, user]);

  // Validate phone number
  const validatePhone = (phone: string): boolean => {
    // Remove all non-digit characters
    const cleanedPhone = phone.replace(/\D/g, '');
    
    // Accept phone numbers that are:
    // - At least 10 digits (standard UK/US number)
    // - At most 15 digits (maximum length for international numbers)
    return cleanedPhone.length >= 10 && cleanedPhone.length <= 15;
  };

  // Handle input changes
  type FormElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

  const handleChange = (e: React.ChangeEvent<FormElement>) => {
    const { name, value } = e.target;
    
    if (name === 'referralCategory') {
      // Reset related fields when referral category changes
      if (value === 'direct') {
        // For direct referrals, set referralType to 'direct'
        setClient(prev => ({
          ...prev,
          referralCategory: value as 'direct' | 'organisation',
          referralType: 'direct',
          organisation: '',
          organisationDisplayName: '',
          numberOfSessions: '',
          sessionRate: ''
        }));
      } else {
        // For organisation referrals, reset fields
        setClient(prev => ({
          ...prev,
          referralCategory: value as 'direct' | 'organisation',
          referralType: '',
          organisation: '',
          organisationDisplayName: '',
          numberOfSessions: '',
          sessionRate: ''
        }));
      }
    } else if (name === 'organisation') {
      // When organisation changes, update session rate, number of sessions, and set referralType to 'organisation'
      const selectedOrg = organisations.find(org => org._id === value);
      if (selectedOrg) {
        setClient(prev => ({
          ...prev,
          organisation: value,
          organisationDisplayName: selectedOrg.displayName,
          referralType: 'organisation',
          sessionRate: selectedOrg.sessionRate?.toString() || '',
          numberOfSessions: selectedOrg.numberOfSessions?.toString() || ''
        }));
      }
    } else if (name === 'referralType') {
      setClient(prev => ({
        ...prev,
        referralType: value,
        sessionRate: ''
      }));
    } else {
      setClient(prev => ({ ...prev, [name]: value }));
    }

    // Clear validation errors
    let validationErrors = { ...errors };
    if (name === 'phone' && validatePhone(value)) delete validationErrors.phone;
    if (name === 'firstName' && value !== '') delete validationErrors.firstName;
    if (name === 'lastName' && value !== '') delete validationErrors.lastName;
    setErrors(validationErrors);
  };

  // Validate form inputs
  const validateForm = (): boolean => {
    const validationErrors: Errors = {};
    if (!client.firstName) validationErrors.firstName = 'First name is required';
    if (!client.lastName) validationErrors.lastName = 'Last name is required';
    if (!client.phone || !validatePhone(client.phone)) {
      validationErrors.phone = 'Please enter a valid phone number';
    }
    
    // Assessment date/time is only required for new clients (not existing clients)
    if (!client.isExistingClient) {
      if (!client.assessmentDateTime) {
        validationErrors.assessmentDateTime = 'Assessment date and time are required';
      } else {
        // Validate that assessment date is in the future
        const assessmentDate = new Date(client.assessmentDateTime);
        const now = new Date();
        if (assessmentDate <= now) {
          validationErrors.assessmentDateTime = 'Assessment date and time must be in the future';
        }
      }
    }
    
    // Counsellor assignment is required for existing clients
    if (client.isExistingClient && !client.currentCounsellorId) {
      validationErrors.currentCounsellorId = 'Please assign a counsellor';
    }
    
    if (client.referralCategory === 'direct' && !client.referralType) {
      validationErrors.referralType = 'Please select a referral type';
    }
    if (client.referralCategory === 'organisation' && !client.organisation) {
      validationErrors.organisation = 'Please select an organisation';
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validateForm()) {
      setSubmitting(true);
      setError('');

      try {
        const response = await api.post('/api/clients', client);
        console.log('Client created:', response.data);

        // For existing clients, redirect to dashboard (counsellor will complete onboarding)
        if (client.isExistingClient) {
          setSnackbar({
            open: true,
            message: 'Existing client created! The assigned counsellor will complete the onboarding.',
            severity: 'success'
          });
          setTimeout(() => {
            navigate('/');
          }, 1500);
        } else {
          // For new clients, create calendar event for the assessment
          const assessmentDate = new Date(client.assessmentDateTime);
          const endDate = new Date(assessmentDate.getTime() + 50 * 60000); // 50 minutes duration

          await api.post('/events', {
            title: `${client.firstName} ${client.lastName}`,
            start: assessmentDate,
            end: endDate,
            type: 'assessment',
            assessor: user?._id,
            client: response.data._id,
            notes: ''
          });

          setSnackbar({
            open: true,
            message: 'Client added successfully!',
            severity: 'success'
          });
          setTimeout(() => {
            navigate('/');
          }, 500);
        }
      } catch (error: any) {
        console.error('Error creating client:', error);
        const errorMessage = error?.response?.data?.error || error.message || 'Failed to create client';
        setError(errorMessage);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const referralTypeOptions = useMemo(() => 
    (referralTypes || [])
      .filter((type) => type && type.active)
      .map((type) => (
        <option key={type._id} value={type.value}>
          {type.displayName}
        </option>
      ))
  , [referralTypes]);

  const organisationOptions = useMemo(() => 
    (organisations || [])
      .filter((org) => org && org.active)
      .map((org) => (
        <option key={org._id} value={org._id}>
          {org.displayName}
        </option>
      ))
  , [organisations]);

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-6 py-8" style={{ overflow: 'visible' }}>
        <div className="glass-card p-6 min-h-[calc(100vh-4rem)]" style={{ overflow: 'visible' }}>
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8" style={{ overflow: 'visible' }}>
          {/* Header Section */}
          <div className="glass-card-light mb-6">
            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-start">
                {/* Buttons on the left */}
                <div className="flex flex-col space-y-4">
                  {/* Primary Actions */}
                  <div className="flex space-x-4">
                    <Button
                      variant="contained"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '160px',
                        backgroundColor: '#2196f3',
                        '&:hover': {
                          backgroundColor: '#1976d2'
                        }
                      }}
                      onClick={() => navigate('/')}
                    >
                      Back to Home
                    </Button>
                  </div>
                </div>

                {/* Title on the right */}
                <div className="text-right">
                  <Typography 
                    variant="h3" 
                    component="h1" 
                    gutterBottom
                    className="theme-text-primary"
                    sx={{
                      fontFamily: 'Roboto',
                      fontWeight: 800,
                      fontSize: '2.5rem',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Client Creation
                  </Typography>
                </div>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" style={{ overflow: 'visible' }}>
            <form onSubmit={handleSubmit} className="space-y-8" style={{ overflow: 'visible' }}>
              {/* Personal Details Section */}
              <div className="glass-card-light rounded-lg p-6" style={{ overflow: 'visible' }}>
                <h3 className="text-lg font-semibold theme-text-primary mb-6">Personal Details</h3>
                
                {/* Existing Client Checkbox */}
                <div className="mb-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={client.isExistingClient}
                      onChange={(e) => setClient({ ...client, isExistingClient: e.target.checked })}
                      className="mr-3 w-5 h-5"
                    />
                    <span className="text-sm font-medium theme-text-primary">Existing Client</span>
                  </label>
                  {client.isExistingClient && (
                    <p className="mt-2 text-sm theme-text-secondary">
                      This client has previously received counselling and will go through a simplified onboarding process.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium theme-text-primary mb-2">First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      value={client.firstName}
                      onChange={handleChange}
                      className="theme-input"
                    />
                    {errors.firstName && <p className="mt-1 text-sm text-red-400">{errors.firstName}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium theme-text-primary mb-2">Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      value={client.lastName}
                      onChange={handleChange}
                      className="theme-input"
                    />
                    {errors.lastName && <p className="mt-1 text-sm text-red-400">{errors.lastName}</p>}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium theme-text-primary mb-2">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={client.phone}
                      onChange={handleChange}
                      className="theme-input"
                    />
                    {errors.phone && <p className="mt-1 text-sm text-red-400">{errors.phone}</p>}
                  </div>

                  {/* Counsellor Assignment - Only for existing clients */}
                  {client.isExistingClient && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium theme-text-primary mb-2">
                        Assign Counsellor <span className="text-red-400">*</span>
                      </label>
                      <select
                        name="currentCounsellorId"
                        value={client.currentCounsellorId}
                        onChange={handleChange}
                        className="theme-input"
                      >
                        <option value="">Select a counsellor...</option>
                        {counsellors.map((counsellor) => (
                          <option key={counsellor._id} value={counsellor._id}>
                            {counsellor.firstName} {counsellor.lastName}
                          </option>
                        ))}
                      </select>
                      {errors.currentCounsellorId && (
                        <p className="mt-1 text-sm text-red-400">{errors.currentCounsellorId}</p>
                      )}
                      <p className="mt-2 text-sm theme-text-secondary">
                        The assigned counsellor will receive an email notification to complete the onboarding process.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Referral Information Section */}
              <div className="glass-card-light rounded-lg p-6" style={{ overflow: 'visible' }}>
                <h3 className="text-lg font-semibold theme-text-primary mb-6">Referral Information</h3>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium theme-text-primary mb-3">Select Referral Type</label>
                  <div className="flex gap-6">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="referralCategory"
                        value="direct"
                        checked={client.referralCategory === 'direct'}
                        onChange={handleChange}
                        className="mr-2 w-4 h-4"
                      />
                      <span className="text-sm theme-text-primary">Direct Referral</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="referralCategory"
                        value="organisation"
                        checked={client.referralCategory === 'organisation'}
                        onChange={handleChange}
                        className="mr-2 w-4 h-4"
                      />
                      <span className="text-sm theme-text-primary">Organisation Referral</span>
                    </label>
                  </div>
                </div>

              {/* Direct Referral Type Selection */}
              {client.referralCategory === 'direct' && (
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium theme-text-primary mb-2">Select Source</label>
                    <select
                      name="referralType"
                      value={client.referralType}
                      onChange={handleChange}
                      className="theme-input"
                      required
                    >
                      <option value="">Select a referral source</option>
                      {referralTypeOptions}
                    </select>
                    {errors.referralType && (
                      <p className="mt-1 text-sm text-red-400">{errors.referralType}</p>
                    )}
                  </div>

                  {client.referralType && (
                    <div>
                      <label className="block text-sm font-medium theme-text-primary mb-2">Session Rate (£)</label>
                      <select
                        name="sessionRate"
                        value={client.sessionRate}
                        onChange={handleChange}
                        className="theme-input"
                        required
                      >
                        <option value="">Select a session rate</option>
                        {sessionRates
                          .sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999))
                          .map(rate => (
                            <option key={rate._id} value={rate.value}>
                              {rate.displayName}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* organisation Selection */}
              {client.referralCategory === 'organisation' && (
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium theme-text-primary mb-2">Select Organisation</label>
                    <select
                      name="organisation"
                      value={client.organisation}
                      onChange={handleChange}
                      className="theme-input"
                      required
                    >
                      <option value="">Select an organisation</option>
                      {organisationOptions}
                    </select>
                    {errors.organisation && (
                      <p className="mt-1 text-sm text-red-400">{errors.organisation}</p>
                    )}
                  </div>

                  {client.organisation && (
                    <>
                      <div>
                        <label className="block text-sm font-medium theme-text-primary mb-2">Number of Sessions</label>
                        <input
                          type="text"
                          value={client.numberOfSessions}
                          className="theme-input opacity-75"
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium theme-text-primary mb-2">Session Rate (£)</label>
                        <input
                          type="text"
                          value={client.sessionRate}
                          className="theme-input opacity-75"
                          readOnly
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
              </div>

              {/* Assessment Information Section - Only for new clients */}
              {!client.isExistingClient && (
              <div className="glass-card-light rounded-lg p-6" style={{ overflow: 'visible', position: 'relative', zIndex: 50 }}>
                <h3 className="text-lg font-semibold theme-text-primary mb-6">Assessment Information</h3>
                
                <div className="space-y-6" style={{ overflow: 'visible' }}>
                  <div>
                    <label className="block text-sm font-medium theme-text-primary mb-2">Initial Assessment (max 250 words)</label>
                    <textarea
                      name="initialAssessment"
                      value={client.initialAssessment}
                      onChange={handleChange}
                      maxLength={250}
                      rows={4}
                      className="theme-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" style={{ overflow: 'visible' }}>
                    <div className="mb-6">
                      <label className="block text-sm font-medium theme-text-primary mb-2">Assessment Date / Time</label>
                      <button
                        type="button"
                        onClick={() => {
                          setTempDate(client.assessmentDateTime ? new Date(client.assessmentDateTime) : new Date());
                          setDatePickerOpen(true);
                        }}
                        className="mt-1 block w-full p-3 border border-white/20 rounded-lg bg-white/5 text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/50 transition-all text-left"
                      >
                        {client.assessmentDateTime 
                          ? new Date(client.assessmentDateTime).toLocaleString('en-GB', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : 'Select date and time'}
                      </button>
                      {errors.assessmentDateTime && (
                        <p className="mt-1 text-sm text-red-400">{errors.assessmentDateTime}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    minWidth: '160px',
                    backgroundColor: '#2196f3',
                    '&:hover': {
                      backgroundColor: '#1976d2'
                    }
                  }}
                >
                  {submitting ? 'Creating...' : 'Create Client'}
                </Button>
              </div>
              {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
            </form>
          </div>
        </div>
        </div>
      </div>

      {/* Date Picker Dialog */}
      <Dialog
        open={datePickerOpen}
        onClose={() => setDatePickerOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" className="theme-text-primary" sx={{ fontWeight: 600 }}>
            Select Assessment Date & Time
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', pb: 2 }}>
          <DatePicker
            selected={tempDate}
            onChange={(date: Date | null) => setTempDate(date)}
            showTimeSelect
            timeFormat="HH:mm"
            timeIntervals={15}
            dateFormat="dd/MM/yyyy HH:mm"
            minDate={new Date()}
            inline
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setDatePickerOpen(false)}
            sx={{ 
              textTransform: 'none', 
              color: 'rgba(255, 255, 255, 0.6)',
              '&:hover': {
                color: 'rgba(255, 255, 255, 0.8)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (tempDate) {
                setClient({ ...client, assessmentDateTime: tempDate.toISOString() });
              }
              setDatePickerOpen(false);
            }}
            variant="contained"
            sx={{ 
              textTransform: 'none',
              background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)'
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeBackground>
  );
}

export default Onboarding;